// stepLineText — the ONE human-readable one-line rendering of a walk step, used
// by both the ProcessTree row's ⋯ "Copy step label" AND the multi-select
// toolbar's Copy (clipboard). Single-sourced here so the copied text is exactly
// what the row shows — no drift between the two copy paths.
//
// Pure (no React, no DOM): a step in → its `Owner > Callee` / `Owner: note` /
// bare-note line out. It is the SELECTION clipboard's payload; pasting these
// lines back through `addStepAfter` re-authors equivalent steps (each line is a
// step operand the recompile invariant guards).

import type { WalkStep } from "@archlang/engine";

/** The one-line label for a step, matching the tree row's visible text.
 *  - call step → `from > to`
 *  - note step with an actor → `from: noteText`
 *  - ownerless prose / bare note → `noteText` */
export function stepLineText(step: WalkStep): string {
  const fromPath = step.from.join(".");
  const toPath = step.to.join(".");
  if (step.noteText !== undefined) {
    return fromPath ? `${fromPath}: ${step.noteText}` : step.noteText;
  }
  return `${fromPath} > ${toPath}`;
}

/** The multi-line clipboard payload for a set of selected step nodeIds, in the
 *  given document order (one step line per output line). Unresolvable /
 *  unselected nodeIds are skipped. */
export function selectedStepsText(
  order: readonly string[],
  selected: ReadonlySet<string>,
  stepByNodeId: (nodeId: string) => WalkStep | undefined,
): string {
  const lines: string[] = [];
  for (const id of order) {
    if (!selected.has(id)) continue;
    const step = stepByNodeId(id);
    if (step) lines.push(stepLineText(step));
  }
  return lines.join("\n");
}
